// Segmentación directa desde tokens del JSON de OCI.
// Reemplaza el pipeline parse-srt + match.ts a partir de Fase 18.
// Este archivo es puro lib/: sin dependencias de UI ni de estado.

import type { OracleToken, TokenSegment } from "./types";

export type SegmentOptions = {
  /** Duración máxima de un segmento en segundos. Default: 5. */
  maxDurationSeconds: number;
  /** Silencio máximo dentro del mismo speaker antes de cortar. Default: 0.8. */
  maxSilenceSeconds: number;
};

const DEFAULT_OPTIONS: SegmentOptions = {
  maxDurationSeconds: 5,
  maxSilenceSeconds: 0.8,
};

/**
 * Agrupa tokens de OCI en segmentos de habla (TokenSegment[]).
 *
 * Reglas de corte (en orden de prioridad):
 * 1. Cambia el speakerIndex → corte obligatorio.
 * 2. Gap entre endTime del token anterior y startTime del siguiente
 *    supera maxSilenceSeconds → corte por silencio.
 * 3. Duración acumulada del segmento supera maxDurationSeconds → corte
 *    por duración.
 *
 * Tokens PUNCTUATION: se concatenan al token anterior sin espacio delante
 * y no generan corte de segmento (se pegan al segmento activo).
 *
 * Tokens sin speakerIndex: fallback a 0.
 */
export function segmentFromTokens(
  tokens: OracleToken[],
  options?: Partial<SegmentOptions>
): TokenSegment[] {
  const opts: SegmentOptions = { ...DEFAULT_OPTIONS, ...options };
  const segments: TokenSegment[] = [];

  if (tokens.length === 0) return segments;

  // Estado del segmento en construcción
  let segTokens: OracleToken[] = [];
  let segStartTime = 0;
  let segEndTime = 0;
  let segSpeaker = 0;
  let lastWordEndTime = 0; // endTime del último token WORD procesado

  const flushSegment = () => {
    if (segTokens.length === 0) return;

    const wordTokens = segTokens.filter((t) => t.type === "WORD");
    const avgConfidence =
      wordTokens.length > 0
        ? wordTokens.reduce((sum, t) => sum + t.confidence, 0) / wordTokens.length
        : 0;

    segments.push({
      index: segments.length,
      startTime: segStartTime,
      endTime: segEndTime,
      speakerIndex: segSpeaker,
      tokens: segTokens,
      avgConfidence,
      text: buildText(segTokens),
    });

    segTokens = [];
  };

  for (const token of tokens) {
    const speaker = token.speakerIndex ?? 0;

    if (token.type === "PUNCTUATION") {
      // Los signos de puntuación siempre van al segmento activo.
      // Si no hay segmento activo todavía, los descartamos (borde raro).
      if (segTokens.length > 0) {
        segTokens.push(token);
      }
      continue;
    }

    // --- Token WORD ---

    if (segTokens.length === 0) {
      // Primer token: abrir segmento nuevo.
      segSpeaker = speaker;
      segStartTime = token.startTime;
      segEndTime = token.endTime;
      lastWordEndTime = token.endTime;
      segTokens.push(token);
      continue;
    }

    // Evaluar si hay que cortar antes de agregar este token.
    const speakerChanged = speaker !== segSpeaker;
    const silence = token.startTime - lastWordEndTime;
    const silenceBreak = silence > opts.maxSilenceSeconds;
    const duration = token.endTime - segStartTime;
    const durationBreak = duration > opts.maxDurationSeconds;

    if (speakerChanged || silenceBreak || durationBreak) {
      flushSegment();
      // Abrir segmento nuevo con este token.
      segSpeaker = speaker;
      segStartTime = token.startTime;
      segEndTime = token.endTime;
      lastWordEndTime = token.endTime;
      segTokens.push(token);
    } else {
      // Continuar segmento activo.
      segEndTime = token.endTime;
      lastWordEndTime = token.endTime;
      segTokens.push(token);
    }
  }

  // Volcar el último segmento.
  flushSegment();

  return segments;
}

/**
 * Concatena los tokens de un segmento en texto legible.
 * Los tokens PUNCTUATION se pegan al token anterior sin espacio delante.
 * Los tokens WORD llevan espacio delante (excepto el primero).
 */
function buildText(tokens: OracleToken[]): string {
  let result = "";
  for (const token of tokens) {
    if (result === "") {
      result = token.token;
    } else if (token.type === "PUNCTUATION") {
      result += token.token;
    } else {
      result += " " + token.token;
    }
  }
  return result;
}
